/**
 * Normalizes para-node's `DashboardResponse` — `{ data, pagination: { offset, limit, total? } }`.
 *
 * NOT interchangeable with `normalizeApiListPayload`: that one returns rows only, so using it
 * here would silently drop the count and leave a server-paginated grid's `rowCount` at 0,
 * stuck on one page. Note also that this envelope nests the count under `pagination`, unlike
 * `GET /v1/gigsafe/admin/webhooks/deliveries`, which returns a flat `{ data, total, … }`.
 *
 * `PaginationQuery.total` is optional on the backend interface, so a missing or non-numeric
 * total falls back to the row count — a single-page grid, never a zero-row one.
 */
export function parseDashboardResponse<T>(payload: unknown): { data: T[]; total: number } {
  if (Array.isArray(payload)) {
    return { data: payload as T[], total: payload.length }
  }

  if (!payload || typeof payload !== 'object') {
    return { data: [], total: 0 }
  }

  const body = payload as { data?: unknown; pagination?: { total?: unknown } }
  const data = Array.isArray(body.data) ? (body.data as T[]) : []
  const total = body.pagination?.total

  if (typeof total === 'number' && Number.isFinite(total)) {
    return { data, total }
  }

  return { data, total: data.length }
}
